import {
  Button,
  Card,
  Col,
  Edit,
  Form,
  Icons,
  Input,
  ListButton,
  Popconfirm,
  Row,
  Space,
  Tag,
  Typography,
  notification,
  useForm,
} from '@pankod/refine-antd';
import { useParams } from '@pankod/refine-react-router-v6';
import { AddTemplateAttributeDrawer, EditTemplateAttributeDrawer } from 'components/drawer';
import { ETemplateAttributeType } from 'enums';
import { useEffect, useState } from 'react';
import {
  IAttribute,
  ITemplate,
  addTemplateAttribute,
  deleteAttribute,
  getTemplate,
  updateTemplate,
  updateTemplateAttribute,
} from 'services/template';
import { capitalize } from 'utilities/shared';

const { Text, Title } = Typography;

export const TemplateEdit: React.FC = () => {
  const [template, setTemplate] = useState<ITemplate | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isAddDrawerVisible, setIsAddDrawerVisible] = useState<boolean>(false);
  const [isEditDrawerVisible, setIsEditDrawerVisible] = useState<boolean>(false);
  const [editAttribute, setEditAttribute] = useState<IAttribute | null>(null);
  const { form, formProps, saveButtonProps } = useForm<ITemplate>({
    resource: 'productTemplate',
  });
  const { id: templateId } = useParams();

  const fetchTemplate = async (id: string) => {
    const response = await getTemplate(id);
    form.setFieldsValue({
      name: response.name,
      description: response.description,
    });
    setTemplate(response);
  };

  useEffect(() => {
    if (!templateId) return;

    (async () => {
      setIsFetching(true);
      try {
        await fetchTemplate(templateId);
      } catch (err: any) {
        console.error(err.message);
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      } finally {
        setIsFetching(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const onFinish = async (values: any) => {
    if (!templateId) return;

    setIsUpdating(true);
    try {
      await updateTemplate(templateId, { name: values.name, description: values.description });
      await fetchTemplate(templateId);
      notification.success({
        message: 'Success',
        description: 'Updated successfully.',
      });
    } catch (err: any) {
      console.error(err.message);
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsUpdating(false);
  };

  const handleDeleteAttribute = async (id: string) => {
    if (!templateId) return;

    try {
      await deleteAttribute(id);
      await fetchTemplate(templateId);
    } catch (err: any) {
      console.error(err.message);
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
  };

  const handleAddAttribute = async (values: any) => {
    if (!templateId) return;

    const data = {
      product_template_id: templateId,
      ...values,
    };
    await addTemplateAttribute(data);
    await fetchTemplate(templateId);
  };

  const handleEditAttribute = async (values: any) => {
    if (!templateId) return;

    const { id, ...rest } = values;
    const data = {
      product_template_id: templateId,
      ...rest,
    };

    await updateTemplateAttribute(id, data);
    await fetchTemplate(templateId);
  };

  const pageHeaders = (
    <Space>
      <ListButton />
    </Space>
  );

  return (
    <>
      <AddTemplateAttributeDrawer
        visible={isAddDrawerVisible}
        onClose={() => setIsAddDrawerVisible(false)}
        onSubmit={handleAddAttribute}
      />
      <EditTemplateAttributeDrawer
        attribute={editAttribute}
        visible={isEditDrawerVisible}
        onClose={() => {
          setIsEditDrawerVisible(false);
          setEditAttribute(null);
        }}
        onSubmit={handleEditAttribute}
      />

      <Edit
        isLoading={isFetching}
        headerProps={{ extra: pageHeaders }}
        saveButtonProps={{ ...saveButtonProps, disabled: isUpdating }}
      >
        <Form
          {...formProps}
          form={form}
          layout="vertical"
          initialValues={{
            isActive: true,
          }}
          onFinish={onFinish}
        >
          <Row justify="space-around" align="middle">
            <Col xs={24} lg={12}>
              <Form.Item
                label="Name"
                name="name"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item label="Description" name="description">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Edit>

      {/* Attributes */}
      <Card
        loading={isFetching}
        title={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Text>Attributes</Text>
            <Button
              size="middle"
              icon={<Icons.PlusOutlined />}
              onClick={() => setIsAddDrawerVisible(true)}
            >
              Add attribute
            </Button>
          </div>
        }
        style={{ marginTop: '1rem' }}
      >
        <Row>
          <Col span={12} offset={6}>
            {template?.productAttribute.map((attribute, index) => {
              const attributeCardTitle = (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text>{capitalize(attribute.name)}</Text>
                  <Space>
                    <Button
                      onClick={() => {
                        setIsEditDrawerVisible(true);
                        setEditAttribute(attribute);
                      }}
                      size="small"
                      icon={<Icons.EditOutlined />}
                    />
                    <Popconfirm
                      title="Are you sure?"
                      okText="Delete"
                      onConfirm={() => handleDeleteAttribute(attribute.id)}
                    >
                      <Button danger size="small" icon={<Icons.DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                </div>
              );

              return (
                <Card
                  key={attribute.id}
                  title={attributeCardTitle}
                  style={{ flex: 1, marginBottom: '20px' }}
                >
                  <Title level={5}>Type:</Title>
                  <Text>{ETemplateAttributeType[attribute.type]}</Text>

                  <Title level={5}>Required:</Title>
                  <Text>{attribute.required ? 'true' : 'false'}</Text>

                  {attribute.type === 'dropdown' && (
                    <>
                      <Title level={5}>Allow Custom Value:</Title>
                      <Text>{attribute.allowCustomValue ? 'true' : 'false'}</Text>
                    </>
                  )}

                  {attribute.type === 'dropdown' && (
                    <>
                      <Title level={5}>Options:</Title>
                      <Text>
                        {attribute.options.map((option, index) => (
                          <Tag key={index}>{option}</Tag>
                        ))}
                      </Text>
                    </>
                  )}
                </Card>
              );
            })}
          </Col>
        </Row>
      </Card>
    </>
  );
};
